import { faAsterisk, faBasketShopping, faCalendar, faHouseDamage, faLayerGroup, faSackDollar, faScaleBalanced, faSeedling, faTimeline, faUser, faWallet, faWeightHanging } from "@fortawesome/free-solid-svg-icons";
import HTMLCode from "components/common/HTMLCode";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import PropertyViewValue from "components/common/views/PropertyViewValue";
import { useTranslation } from "react-i18next";
import { SaleViewPageQuery } from "types/graphql";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import PersonPropertyView from "../../app/person/PersonPropertyView";
import WalletPropertyView from "components/entities/app/person/wallet/WalletPropertyView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import If from "components/common/If";

export type SaleViewPropsType = {
    model: Exclude<SaleViewPageQuery["sale"], null | undefined>
    view: 'compact' | 'default',
    name: string
}

const SaleView = (props: SaleViewPropsType) => {
    const [t] = useTranslation();

    const properties: ItemViewPropsType['model'] = [
        {
            items: [
                {
                    icon: faAsterisk,
                    label: 'fields.id.shortName',
                    value: props.model.id,
                    hide: 'compact',
                    name: "id"
                },
                {
                    icon: faSeedling,
                    label: 'fields.fruit.shortName',
                    value: props.model.fruit?.name,
                    name: 'fruit'
                }
            ],
            name: 'id',
            hide: 'compact'
        },
        {
            items: [
                {
                    icon: faCalendar,
                    label: 'fields.date.shortName',
                    name: 'date',
                    value: DateUtil.format(props.model?.date, DateFormats.SHORT)
                },
                {
                    label: 'fields.person.shortName',
                    name: 'person',
                    value: <>
                        <If condition={props.view === 'default'}>
                            <PersonPropertyView
                                personName={props.model.person.name}
                                personId={props.model.person.id}
                                open
                                name={props.name + "-sale-view"}
                            />
                        </If>
                        <If condition={props.view === 'compact'}>
                            <div className={`grid grid-cols-3 whitespace-nowrap text-center`}>
                                <div className="text-right">
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <PersonPropertyView
                                    personName={props.model.person.name}
                                    personId={props.model.person.id}
                                    name={props.name + "-sale-view"}
                                />
                                <div className="text-left">
                                    <If condition={props.model.wallet}>
                                        <FontAwesomeIcon icon={faWallet} className={props.model.wallet?.archived ? 'text-neutral-400 dark:text-neutral-500' : ''} />
                                    </If>
                                </div>
                            </div>
                        </If>
                    </>
                },
                {
                    icon: faWallet,
                    label: 'fields.wallet.shortName',
                    name: 'wallet',
                    hide: props.view === 'compact',
                    value: props.model.wallet ? <WalletPropertyView open model={props.model.wallet!} /> : '-'
                }
            ],
            name: 'data-person',
            className: {
                compact: 'grid grid-cols-2 text-center'
            }
        },
        {
            items: [
                {
                    icon: faWeightHanging,
                    label: 'fields.kg.shortName',
                    name: 'kg',
                    value: <>{formatMoney(props.model?.kg)} kg</>
                },
                {
                    icon: faScaleBalanced,
                    label: 'fields.price-per-kg.shortName',
                    name: 'price',
                    value: <>{formatMoney(props.model?.pricePerKg)} <HTMLCode code="euro" /></>
                },
                {
                    icon: faSackDollar,
                    label: 'fields.total.shortName',
                    name: 'total',
                    value: <>{formatMoney(props.model?.total)} <HTMLCode code="euro" /></>
                }
            ],
            name: 'quantity',
        },
        {
            items: [
                {
                    icon: faLayerGroup,
                    label: 'fields.category.shortName',
                    name: 'category',
                    value: props.model?.category?.name ? t('agriculture.category.' + props.model?.category?.name.toLowerCase()) : '-',
                },
                {
                    label: 'fields.type.shortName',
                    name: 'saleType',
                    value: props.model?.type ? t('agriculture.saleTypes.' + props.model.type.toLowerCase()) : '-',
                    hide: 'compact'
                },
                {
                    icon: faBasketShopping,
                    label: 'fields.cart.shortName',
                    name: 'cart',
                    value: props.model?.cartQuantity,
                },
                {
                    icon: faHouseDamage,
                    label: 'fields.chamber.shortName',
                    name: 'chamber',
                    value: <PropertyViewValue value={props.model?.chamber ? true : false} />,
                    hide: 'compact'
                },
                {
                    icon: faTimeline,
                    label: 'fields.season.shortName',
                    name: 'season',
                    value: props.model?.season?.name
                },
            ],
            name: 'other-info',
        },
        {
            items: [
                {
                    label: 'fields.note.name',
                    name: 'note',
                    value: props.model.note,
                    hide: "compact",
                    propertyType: 'note'
                }
            ],
            hide: "compact",
            name: 'note'
        }
    ];

    return <ItemView name={props.name} model={properties} view={props.view} />

};

export default SaleView;
